import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import type { Membership } from '@h2h/shared';
import { useAuth } from '../../../context/AuthContext';
import { useLeagues, useMyMemberships, useUnreadByLeague } from '../../../hooks/useQueries';
import { identityFor } from '../../../data/leagues';
import { useHaptics } from '../../../hooks/useHaptics';
import { SPRING_POP, useReducedMotion } from '../../../lib/motion';
import { cn } from '../../../lib/cn';
import { ChevronDown } from '../icons';
import { useLeagueSwitch } from '../league-switch/LeagueSwitchTransition';
import { LeagueCrest } from './LeagueCrest';
import { LeagueSwitcherDropdown } from './LeagueSwitcherDropdown';
import { LeagueSwitcherSheet } from './LeagueSwitcherSheet';
import type { LeagueTab } from './types';

/**
 * The tappable "which league am I in" header. Shows the current league's flag
 * emblem + name and opens a sheet to hop to another joined world (or out to the
 * lobby). Switching keeps you on the same tab — e.g. Profile in World Cup → Profile in the
 * league you pick. This is the in-world counterpart to the back-arrow to Home.
 */
export function LeagueSwitcher({
  leagueId,
  tab = null,
  subtitle,
  size = 'md',
  variant = 'light',
  className,
  /** When set, only these memberships appear in the switcher (e.g. leagues you share with a profile). */
  memberships: membershipsOverride,
  /** Override navigation when picking a league (e.g. update ?league= on /u/:id). */
  onSelectLeague
}: {
  leagueId: string;
  tab?: LeagueTab;
  subtitle?: ReactNode;
  /** `icon` — crest-only chip for the slim tablet nav rail. */
  size?: 'icon' | 'sm' | 'md';
  variant?: 'light' | 'dark';
  className?: string;
  memberships?: Membership[];
  onSelectLeague?: (leagueId: string) => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const reduce = useReducedMotion();
  const playLeagueSwitch = useLeagueSwitch();
  const { impact } = useHaptics();
  const { uid } = useAuth();
  const { data: leagues } = useLeagues();
  const { data: memberships } = useMyMemberships(uid);
  const unreadByLeague = useUnreadByLeague(uid);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  // Did we arrive here from a league switch? Captured once at mount from the nav
  // state `goTo` stamps on, so the crest only flourishes on a real switch — never on
  // ordinary navigation into the hub (back, tab change, deep link).
  const [justSwitched] = useState(
    () => Boolean((location.state as { fromLeagueSwitch?: boolean } | null)?.fromLeagueSwitch)
  );
  const nameFlourish = justSwitched && !reduce;

  const league = leagues?.find((l) => l.leagueId === leagueId);
  const identity = identityFor(leagueId);
  const name = league?.name ?? identity?.name ?? 'League';
  const dark = variant === 'dark';
  const newsElsewhere = Object.entries(unreadByLeague).some(([id, count]) => id !== leagueId && count > 0);

  const goTo = (targetId: string) => {
    setOpen(false);
    if (targetId === leagueId) return;
    impact('selection');
    if (onSelectLeague) {
      onSelectLeague(targetId);
      return;
    }
    // Play the "entering a new world" overlay; it runs the actual navigation once it
    // has covered the screen. The nav state still flags the arrival crest flourish.
    playLeagueSwitch(targetId, () =>
      navigate(`/play/${targetId}${tab ? `/${tab}` : ''}`, { state: { fromLeagueSwitch: true } })
    );
  };

  const listMemberships = membershipsOverride ?? memberships ?? [];

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const sheetProps = {
    activeId: leagueId,
    memberships: listMemberships,
    leagues,
    unreadByLeague,
    onClose: () => setOpen(false),
    onSelect: goTo,
    onAll: () => {
      setOpen(false);
      impact('light');
      navigate('/football');
    }
  };

  return (
    <div ref={anchorRef} className={cn('relative w-full', className)}>
      {size === 'icon' ? (
        <button
          type="button"
          aria-label={`Switch league — ${name}`}
          aria-expanded={open}
          onClick={() => {
            impact('light');
            setOpen((v) => !v);
          }}
          className={cn(
            'tap relative mx-auto grid h-11 w-11 place-items-center rounded-2xl transition-colors',
            dark
              ? 'border border-white/15 bg-white/10 hover:bg-white/[0.16]'
              : 'border border-black/[0.06] bg-white shadow-soft hover:bg-wash',
            open && !dark && 'bg-wash ring-1 ring-black/[0.04]'
          )}
        >
          <LeagueCrest
            flourish={justSwitched}
            leagueId={leagueId}
            country={league?.country ?? identity?.country}
            slug={league?.cycling?.slug}
            size="chip"
          />
          {newsElsewhere && (
            <span
              className={cn(
                'absolute right-1 top-1 h-[7px] w-[7px] rounded-full bg-accent ring-2',
                dark ? 'ring-white/10' : 'ring-white'
              )}
              aria-label="News in another league"
            />
          )}
        </button>
      ) : size === 'sm' ? (
        <button
          type="button"
          aria-label="Switch league"
          aria-expanded={open}
          onClick={() => {
            impact('light');
            setOpen((v) => !v);
          }}
          className={cn(
            'tap group inline-flex w-full max-w-full items-center gap-2 rounded-full py-[5px] pl-[5px] pr-3 transition-colors',
            dark
              ? 'border border-white/15 bg-white/10 hover:bg-white/[0.16]'
              : 'border border-black/[0.06] bg-white shadow-soft hover:bg-wash',
            open && !dark && 'bg-wash ring-1 ring-black/[0.04]'
          )}
        >
          <LeagueCrest
            flourish={justSwitched}
            leagueId={leagueId}
            country={league?.country ?? identity?.country}
            slug={league?.cycling?.slug}
            size="chip"
          />
          <span className={cn('min-w-0 flex-1 truncate text-left text-[12.5px] font-extrabold tracking-snug', dark ? 'text-white' : 'text-ink')}>
            {name}
          </span>
          {subtitle && (
            <span className={cn('shrink-0 text-[11px] font-semibold', dark ? 'text-white/55' : 'text-ink2')}>
              · {subtitle}
            </span>
          )}
          <ChevronDown
            width={15}
            height={15}
            strokeWidth={2.6}
            className={cn(
              'shrink-0 transition-transform duration-200',
              open && 'rotate-180',
              dark ? 'text-white/60' : 'text-ink3'
            )}
          />
          {newsElsewhere && (
            <span
              className={cn(
                'h-[7px] w-[7px] shrink-0 rounded-full bg-accent ring-2',
                dark ? 'ring-white/10' : 'ring-white'
              )}
              aria-label="News in another league"
            />
          )}
        </button>
      ) : (
        <button
          type="button"
          aria-label="Switch league"
          aria-expanded={open}
          onClick={() => {
            impact('light');
            setOpen((v) => !v);
          }}
          className="tap group flex w-full items-center gap-[13px] text-left"
        >
          <LeagueCrest
            flourish={justSwitched}
            leagueId={leagueId}
            country={league?.country ?? identity?.country}
            slug={league?.cycling?.slug}
            size="lg"
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <motion.span
                className={cn(
                  'truncate text-[24px] font-extrabold tracking-[-0.025em] leading-[1.05]',
                  dark ? 'text-white' : 'text-ink'
                )}
                initial={nameFlourish ? { opacity: 0, x: 10 } : false}
                animate={nameFlourish ? { opacity: 1, x: 0 } : undefined}
                transition={{ ...SPRING_POP, delay: 0.04 }}
              >
                {name}
              </motion.span>
              <span
                className={cn(
                  'grid h-[22px] w-[22px] shrink-0 place-items-center rounded-full transition-all duration-200 group-active:scale-95',
                  open && !dark && 'bg-fill2 text-ink',
                  dark ? 'bg-white/12 text-white/75' : 'bg-fill text-ink2 group-hover:bg-fill2'
                )}
              >
                <ChevronDown width={15} height={15} strokeWidth={2.6} className={cn('transition-transform duration-200', open && 'rotate-180')} />
              </span>
              {newsElsewhere && (
                <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-accent" aria-label="News in another league" />
              )}
            </div>
            {subtitle && (
              <div className={cn('truncate text-[13px] font-medium', dark ? 'text-white/60' : 'text-ink2')}>{subtitle}</div>
            )}
          </div>
        </button>
      )}

      <LeagueSwitcherSheet open={open} {...sheetProps} />
      {open && <LeagueSwitcherDropdown {...sheetProps} anchorRef={anchorRef} />}
    </div>
  );
}
